import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { _API_URL } from '@/actions/constants';
import { CHECK_ERROR } from '@/components/alert/Alert';

// Lazy import to avoid circular dependency with BaseService
const getAuthenticateService = () => import('./Authenticate.service').then(m => m.default);

const axiosInstance = axios.create({ headers: { 'X-GDT-Dev-Token': import.meta.env.VITE_APP_DEV_TOKEN || '' } });
function containsParentheses(inputString: string) {
  const regex = /\(([^)]{0,7})\)/;
  return regex.test(inputString);
}
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error.response || 'Something went wrong!');
  },
);
interface PropCallback {
  config: AxiosRequestConfig;
  logout: () => void;
}

export const callApi = async (url: string, method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', body?: any) => {
  try {
    const response = await axiosInstance({
      url: `${_API_URL}${url}`,
      method: method ? method : 'GET',
      data: method !== 'GET' ? body : undefined, // Chỉ đính kèm body nếu không phải GET
    });
    return response.data;
  } catch (error: any) {
    // Lấy thông tin response từ lỗi
    const status = error?.status;
    const data = error?.data;

    switch (status) {
      case 401:
        getAuthenticateService().then(AuthService => AuthService.logoutAccount());
        break;
      default:
        if (data) {
          throw data;
        } else {
          throw new Error('Đã có lỗi xảy ra');
        }
    }

    throw error;
  }
};

/**
 * Gọi Fetch API phương thức GET
 * @param {string} url // Đường dẫn chính xác của API
 * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
 */
export async function GetMappingAuthorization<T>(
  url: string,
  conf: AxiosRequestConfig,
  callBack?: PropCallback,
): Promise<T> {
  try {
    const res = await axiosInstance.get<AxiosResponse<T>>(_API_URL + url, conf);
    delete res.data['@odata.context'];
    if (res.config.method === 'get') {
      if (containsParentheses(url)) return res.data as T;
      const { value } = res.data as any;

      if (value) {
        if (url.indexOf('$count=true') > -1) {
          return { count: res.data['@odata.count'], value };
        } else {
          return value;
        }
      }
      return res.data as T;
    }
    return res.data as T;
  } catch (error: any) {
    // Access to config, request, and response
    switch (error.status) {
      case 401:
        callBack && callBack.logout();
        break;
      default:
        error?.data?.detail && CHECK_ERROR(error?.data?.detail);
        throw new Error();
    }
    // Trường hợp không có lỗi, bạn có thể trả về một giá trị mặc định hoặc undefined
    return Promise.reject('No result');
  }
}

// /**
//  * Gọi Fetch API phương thức GET không cần đăng nhập
//  * @param {string} url // Đường dẫn chính xác của API
//  * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
//  */
// export function GetMappingPublic(url, conf) {
//   return exportFetch({...conf, prefix: _API_URL, method: 'GET', url, body: null});
// }

// /**
//  * Gọi Fetch API phương thức GET
//  * @param {string} url // Đường dẫn chính xác của API
//  * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
//  */
// export function GetMappingAuthorization(url, conf) {
//   return exportFetch({...conf, prefix: _API_URL, method: 'GET', url, body: null});
// }

/**
 * Gọi Fetch API phương thức POST WTIH CONFIG
 * @param {String} url // Đường dẫn chính xác của API
 * @param {BodyInit | null} body // Dữ liệu gửi lên API
 * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
 */
export async function PostConfig<T>(url: string, body: any): Promise<T> {
  axiosInstance.defaults.headers['Accept'] = 'application/json';
  axiosInstance.defaults.withCredentials = true;
  const res = await axiosInstance.post<T>(_API_URL + url, body);
  return res.data;
}

/**
 * Gọi Fetch API phương thức POST
 * @param {String} url // Đường dẫn chính xác của API
 * @param {BodyInit | null} body // Dữ liệu gửi lên API
 * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
 */
export async function PostMappingAuthorization<T>(url: string, body: any, callBack?: PropCallback): Promise<T> {
  try {
    const res = await axiosInstance.post<T>(_API_URL + url, body, callBack?.config);
    return res.data;
  } catch (error: any) {
    // Access to config, request, and response
    switch (error.status) {
      case 401:
        if (!url.includes('Login')) callBack && callBack.logout();
        CHECK_ERROR(error?.data?.detail);
        throw new Error(error);
      case 409:
        CHECK_ERROR(error?.data?.detail);
        throw new Error(error);
      case 300:
        return Promise.reject({ code: 'CHANGEPASS', message: error?.data?.message });
      default:
        error?.data?.message && CHECK_ERROR(error?.data?.message);
        throw new Error(error.data);
    }
  }
}

// /**
//  * Gọi Fetch API phương thức POST BATCH
//  * @param {String} url // Đường dẫn chính xác của API
//  * @param {BodyInit | null} body // Dữ liệu gửi lên API
//  * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
//  */

// export function BatchAuthorization(urlBatch, data, endpoint) {
//   // const body = CreateListFetch(urlBatch, url, data);
//   return exportFetch({
//     prefix: _API_URL,
//     method: 'POST',
//     url: urlBatch + '/$batch',
//     body: data,
//     endpoint,
//     isBatch: true,
//   });
// }

/**
 * Gọi Fetch API phương thức PATCH
 * @param {String} conf.url // Đường dẫn chính xác của API
 * @param {BodyInit | null} body // Dữ liệu gửi lên API
 * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
 */
export function PatchMappingAuthorization<T>(url: string, body: any, callBack?: PropCallback) {
  return axiosInstance.patch<T>(_API_URL + url, body);
}

/**
 * Gọi Fetch API phương thức PUT
 * @param {String} conf.url // Đường dẫn chính xác của API
 * @param {BodyInit | null} body // Dữ liệu gửi lên API
 * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
 */
export function PutMappingAuthorization<T>(url: string, body: any, callBack?: PropCallback) {
  return axiosInstance.put<T>(_API_URL + url, body);
}

/**
 * Gọi Fetch API phương thức DELETE
 *
 * @param {String} url // Đường dẫn chính xác của API
 * @param {Pick<ExportFetchType, 'noAlert' | 'responseType'>} conf Tùy chỉnh fetch API
 */
export async function DeleteMappingAuthorization<T>(url: string, body: any, callBack?: PropCallback): Promise<T> {
  const res = await axiosInstance.delete<T>(_API_URL + url, { data: body });
  return res.data;
}

export default axiosInstance;
